import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Pill } from './Pill';
import { parkingVerdict } from '../services/verdict';
import { bestHeightM } from '../types';
import type { Parking } from '../types';

export interface ParkingWithDistance extends Parking {
  distanceKm: number;
}

interface ResultsPanelProps {
  parkings: ParkingWithDistance[];
  carHeightM: number;
  highlightedId?: string | null;
  onHoverItem?: (id: string | null) => void;
  className?: string;
}

/**
 * Sidebar list (desktop, ~360px per Q19) — also reused as the content of
 * ResultsBottomSheet on mobile. Sorted by distance ascending; unfit parkings
 * are greyed, not hidden (Q19).
 */
export const ResultsPanel: React.FC<ResultsPanelProps> = ({
  parkings,
  carHeightM,
  highlightedId,
  onHoverItem,
  className,
}) => {
  const location = useLocation();
  const sorted = [...parkings].sort((a, b) => a.distanceKm - b.distanceKm);

  if (sorted.length === 0) {
    return (
      <div className={className}>
        <div className="p-s5 text-center text-[12px] text-ink3 font-sans">
          No parkings mapped here yet.
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {sorted.map((parking) => {
        const verdict = parkingVerdict(parking, carHeightM);
        const heightM = bestHeightM(parking);
        const doesntFit = verdict.state === 'doesnt_fit';
        return (
          <Link
            key={parking.id}
            to={`/parking/${parking.id}${location.search}`}
            onMouseEnter={() => onHoverItem?.(parking.id)}
            onMouseLeave={() => onHoverItem?.(null)}
            className={`flex items-center gap-s3 py-s4 px-s4 border-b border-rule-soft transition-colors ${
              highlightedId === parking.id ? 'bg-tint' : 'bg-card hover:bg-tint'
            } ${doesntFit ? 'opacity-50' : ''}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-serif text-[16px] text-ink1 truncate">{parking.name}</span>
                <Pill tier={verdict.tierColor} label={verdict.badge} size="sm" />
              </div>
              <div className="text-[11px] text-ink3 font-sans truncate">
                {parking.address ?? `${parking.lat.toFixed(4)}, ${parking.lng.toFixed(4)}`}
              </div>
              <div className="text-[10px] text-ink3 font-mono mt-1 tracking-wide">
                {heightM != null ? `${heightM.toFixed(2)} M` : 'NO DATA'} · {parking.distanceKm.toFixed(1)} KM
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};
